import React, { useState, useEffect } from "react";
import { 
	Col,
	Select,
	Form,
} from "antd";

import { 
	ivaRegimenExento, 
	taxesByDte,
} from "../../../../services";
import { formatNumber } from "../../../../utils/Formaters";

import PropTypes from "prop-types";

// Función para calcular el valor del impuesto basado en otros valores del formulario
export const calculateTaxValue = (tax, quantity, price, discount = 0, currency, xchangeRate, company) => {
	let montoGravable = ((quantity * price) - discount) / 1.12;
	if (ivaRegimenExento.includes(company.tax_authority_specifications.iva_regimen)) {
		montoGravable = (quantity * price) - discount;
	}

	if (!tax) return 0;

	// Impuesto sobre la Unidad
	if (tax[3] === 1) {
		if (tax[4] === currency || tax[4] === undefined) {
			return parseFloat((tax[2] * quantity).toFixed(5));
		} else if (currency === "GTQ" && tax[4] === "USD") {
			return parseFloat((tax[2] * quantity * xchangeRate).toFixed(5));
		} else if (currency === "USD" && tax[4] === "GTQ") {
			return parseFloat(((tax[2] * quantity) / xchangeRate).toFixed(5));
		}
		// Impuesto sobre el Total basado en el monto gravable del IVA si IVA está procesado, de lo contrario usa el monto gravable estándar
	} else if (tax[3] === 100) {
		return parseFloat((montoGravable * (tax[2] / 100)).toFixed(5));
	}

	// Devuelve 0 si no se cumple ninguna condición
	return 0;
};

export const TaxSelector = ({ field, form, company, changes, handleFieldChange, exchangeRate }) => {
	const [taxOptions, setTaxOptions] = useState([]);
	const [selectedTaxes, setSelectedTaxes] = useState(
		ivaRegimenExento.includes(company.tax_authority_specifications.iva_regimen) ? ["IVA EXENTO"] : ["IVA"]
	);

	// Efecto para calcular y actualizar los valores de los impuestos cuando cambien los valores del formulario
	useEffect(() => {
		const dteType = form.getFieldValue("type")? form.getFieldValue("type") : "";
		const quantity = form.getFieldValue(["items", field.name, "quantity"]);
		const price = form.getFieldValue(["items", field.name, "priceUnit"]);
		const discount = form.getFieldValue(["items", field.name, "discount"]);
		const currency = form.getFieldValue("currency");

		if (dteType == undefined || quantity == undefined || price == undefined || discount == undefined || currency == undefined) {
			return; 
		}
        
		// Iterar sobre las claves del diccionario taxesByDte
		const updatedTaxOptions = Object.keys(taxesByDte[dteType]).map(taxKey => {
			const taxDetails = taxesByDte[dteType][taxKey];
			const value = calculateTaxValue(taxDetails, quantity, price, discount, currency, exchangeRate, company);

			// Verifica si el valor es un número antes de usar toFixed
			const displayValue = isNaN(value) ? "0.00" : formatNumber(value.toFixed(2));
			const displayName = `${taxKey} (${displayValue})`;

			let finalTax = { label: displayName, value: taxKey };
			if (taxKey === "IVA" || taxKey === "IVA EXENTO") {
				finalTax.disabled = true;
			}

			return finalTax;
		});

		setTaxOptions(updatedTaxOptions);
	}, [changes, form, field.name, company]);

	// Efecto para actualizar el valor predeterminado en el formulario
	useEffect(() => {
		const dteType = form.getFieldValue("type")? form.getFieldValue("type"): "";  

		let defaultValue = ivaRegimenExento.includes(company.tax_authority_specifications.iva_regimen) ? ["IVA EXENTO"] : ["IVA"];
		const currentItems = form.getFieldValue("items");

		if (!Object.keys(taxesByDte[dteType]).includes(defaultValue[0])){
			defaultValue = [];
		}
        
		// Clonar el objeto de items actual para no mutarlo directamente
		const updatedItems = [...currentItems];
        
		// Establecer el valor predeterminado solo si el campo 'taxes' está vacío o no está definido
		updatedItems[field.name] = {
			...updatedItems[field.name],
			taxes: defaultValue
		};

		// Actualizar todo el campo 'items' con los nuevos valores
		form.setFieldsValue({ items: updatedItems });
	}, [form.getFieldValue("type")]);

	// Manejar el cambio en el Select
	const handleSelectChange = (value) => {
		// Actualizar el estado local
		setSelectedTaxes(value);

		// Actualizar el formulario con el valor seleccionado
		const currentItems = form.getFieldValue("items");
		const updatedItems = [...currentItems];
		updatedItems[field.name] = {
			...updatedItems[field.name],
			taxes: value
		};
		form.setFieldsValue({ items: updatedItems });

		// Llamar a la función de manejo de cambios adicional
		handleFieldChange("refresh", "", "");

	};

	return (
		<Col span={12}>
			<Form.Item
				{...field}
				name={[field.name, "taxes"]}
				rules={[{ required: false }]}
			>
				<Select
					mode="multiple"
					style={{ width: "100%" }}
					placeholder="Impuestos"
					options={taxOptions}
					value={selectedTaxes}
					onChange={handleSelectChange}
				/>
			</Form.Item>
		</Col>
	);
};

TaxSelector.propTypes = {
	field: PropTypes.shape({
		name: PropTypes.number.isRequired,
	}).isRequired,
	form: PropTypes.any.isRequired,
	company: PropTypes.shape({
		tax_authority_specifications: PropTypes.shape({
			iva_regimen: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
	changes: PropTypes.number, // You can define the shape of this object based on your needs
	handleFieldChange: PropTypes.func.isRequired,
	exchangeRate: PropTypes.number.isRequired,
};